import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { TabMenu } from "primereact/tabmenu";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL } from "../../style";
import FieldNote from "./common/FieldNote";
import WebsiteMediaInput from "./WebsiteMediaInput";

export default function WebsiteNotificationNotice() {
    const { noticeId } = useParams();
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [notice, setNotice] = useState();
    const [images, setImages] = useState([]);
    const [files, setFiles] = useState([]);
    const [tab, setTab] = useState("photos");
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        setLoading(true);
        requestAPI({
            requestPath: "website/notifications",
            requestMethod: "GET",
            onRequestFailure: () => {
                setError("Couldn't load this notice");
                setLoading(false);
            },
            onResponseReceieved: (notices, responseCode) => {
                if (!notices || responseCode !== 200) {
                    setError("Couldn't load this notice");
                    setLoading(false);
                    return;
                }
                const match = notices.find((item) => String(item.id) === String(noticeId));
                if (!match) {
                    setError("This notice was not found");
                    setLoading(false);
                    return;
                }
                setNotice(match);
                requestAPI({
                    requestPath: "website/notification-images",
                    requestMethod: "GET",
                    onRequestFailure: () => {
                        setError("Couldn't load photos");
                        setLoading(false);
                    },
                    onResponseReceieved: (photoRows, photoCode) => {
                        if (!photoRows || photoCode !== 200) {
                            setError("Couldn't load photos");
                            setLoading(false);
                            return;
                        }
                        setImages(photoRows.filter((item) => item.notification_public_id === match.public_id));
                        requestAPI({
                            requestPath: "website/notification-files",
                            requestMethod: "GET",
                            onRequestFailure: () => {
                                setError("Couldn't load files");
                                setLoading(false);
                            },
                            onResponseReceieved: (fileRows, fileCode) => {
                                if (fileRows && fileCode === 200) setFiles(fileRows.filter((item) => item.notification_public_id === match.public_id));
                                else setError("Couldn't load files");
                                setLoading(false);
                            },
                        });
                    },
                });
            },
        });
    }, [noticeId, requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const photos = tab === "photos";
    const visible = photos ? images : files;

    return (
        <div className="flex flex-column h-full">
            <PageTitle title={notice?.title || "Notice"} />
            <TabHeader
                className="px-3 pt-3"
                title={notice?.title || "Notice"}
                highlights={["Extra photos and files for this notice. The cover, video, and main PDF are edited with the pencil on the notice list"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: { notification_public_id: notice?.public_id, file_type: "pdf" } })} />,
                ]}
            />
            <TabMenu
                model={[
                    { label: "Photos", icon: "pi pi-image", command: () => setTab("photos") },
                    { label: "Files", icon: "pi pi-file", command: () => setTab("files") },
                ]}
                activeIndex={photos ? 0 : 1}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {visible.length ? visible.map((row) => (
                        <Row key={`${tab}-${row.id}`} row={row} photos={photos} setImages={setImages} setFiles={setFiles} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? (
                <Editor dialog={dialog} photos={photos} setDialog={setDialog} setImages={setImages} setFiles={setFiles} requestAPI={requestAPI} showToast={showToast} />
            ) : null}
        </div>
    );
}

function Row({ row, photos, setImages, setFiles, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: photos ? `website/notification-images/${row.id}` : `website/notification-files/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: photos ? "Photo removed" : "File removed", life: 1000 });
                    const update = (prev) => prev?.filter((item) => item.id !== row.id);
                    if (photos) setImages(update);
                    else setFiles(update);
                } else showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
            },
        });
    };
    return (
        <div className="flex gap-3 align-items-center border-round py-2 px-3 text-white bg-green-500">
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{photos ? (row.alt_text || "Photo") : (row.title || "File")}</span>
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, photos, setDialog, setImages, setFiles, requestAPI, showToast }) {
    const [record, setRecord] = useState({ file_type: "pdf", ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        if (!String(record.url || "").trim()) {
            showToast({ severity: "error", summary: "File required", detail: photos ? "Upload the photo" : "Upload the file", life: 2000 });
            return;
        }
        const body = photos ? record : { ...record, title: String(record.title || "").trim() || "PDF Document", file_type: record.file_type || "pdf" };
        requestAPI({
            requestPath: photos ? "website/notification-images" : "website/notification-files",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: body,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    const update = (prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item)));
                    if (photos) setImages(update);
                    else setFiles(update);
                    showToast({ severity: "success", summary: "Saved", detail: photos ? "Photo saved" : "File saved", life: 1000 });
                    setDialog({ visible: false });
                } else showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
            },
        });
    };
    return (
        <Dialog header={isNew ? (photos ? "Add a photo" : "Add a file") : (photos ? "Edit photo" : "Edit file")} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                {photos ? (
                    <>
                        <WebsiteMediaInput label="Photo" field="url" hint="Required size: 1280 ? 720 px (16:9). A larger photo is scaled to fit. The whole photo stays visible on the notice page." record={record} setRecord={setRecord} disabled={saving} />
                        <div>
                            <FloatLabel className="mt-5">
                                <InputText id="alt_text" className="w-full" value={record?.alt_text ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, alt_text: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                                <label htmlFor="alt_text">Photo description</label>
                            </FloatLabel>
                            <FieldNote text="Read by screen readers. This is also the name in this list." />
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <FloatLabel className="mt-5">
                                <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                                <label htmlFor="title">File name</label>
                            </FloatLabel>
                            <FieldNote text="Shown under Attachments. Leave empty to show PDF Document." />
                        </div>
                        <WebsiteMediaInput label="File" type="pdf" field="url" hint="This file appears under Attachments on the notice page." record={record} setRecord={setRecord} disabled={saving} />
                        <div>
                            <FloatLabel className="mt-5">
                                <Dropdown inputId="file_type" className="w-full" options={[{ label: "PDF", value: "pdf" }, { label: "Other file", value: "file" }]} optionLabel="label" optionValue="value" value={record?.file_type || "pdf"} onChange={(event) => setRecord((prev) => ({ ...prev, file_type: event.value }))} disabled={saving} />
                                <label htmlFor="file_type">File kind</label>
                            </FloatLabel>
                            <FieldNote text="PDF shows a PDF icon. Other file shows a paperclip." />
                        </div>
                        <div>
                            <FloatLabel className="mt-5">
                                <InputText id="file_size" className="w-full" value={record?.file_size ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, file_size: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                                <label htmlFor="file_size">File size</label>
                            </FloatLabel>
                            <FieldNote text="Optional. Example: 240 KB." />
                        </div>
                    </>
                )}
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
